import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Paper,
    Grid
} from '@mui/material';
import { SmsService } from '../../service/smsService';
import { SmsTemplateService } from '../../service/smsTemplateService';
import { toast } from 'react-toastify';
import { toLocalDateInputValue } from './utils';
import { usePreferences } from 'hooks/usePreferences';

export default function SendDayRemindersDialog({ open, date, locationId, locationName, onClose, onSuccess }) {
      const { preferences } = usePreferences();
      const tenantTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;    
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    // Load templates on mount
    useEffect(() => {
        if (open) {
            loadTemplates();
            setError('');
            setSelectedTemplateId('');
            setValidationResult(null);
        }
    }, [open]);

    const loadTemplates = async () => {
        setTemplateLoading(true);
        try {
            const response = await SmsTemplateService.getTemplates();
            setTemplates(response.data || []);
        } catch (err) {
            console.error('Failed to load templates:', err);
            setTemplates([]);
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleValidate = async () => {
        if (!date || !locationId) return;

        setValidationLoading(true);
        setError('');

        try {
            const dateStr = toLocalDateInputValue(date, tenantTimezone);
            const payload = {
                filterType: 'appointments_custom_range',
                filterStartDate: dateStr,
                filterEndDate: dateStr,
                locationId: locationId
            };

            const response = await SmsService.validateBulkFilters(payload);

            if (response?.code === 0) {
                setValidationResult(response.data);
                setError('');
            } else {
                setError(response?.message || 'Validation failed');
            }
        } catch (err) {
            setError(err.message || 'Failed to validate');
            console.error('Validation error:', err);
        } finally {
            setValidationLoading(false);
        }
    };

    // Auto-validate when template is selected
    useEffect(() => {
        if (selectedTemplateId && date && locationId) {
            handleValidate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplateId, date, locationId]);

    const handleSend = async () => {
        if (!selectedTemplateId) {
            setError('Please select a template');
            return;
        }

        if (!date || !locationId) {
            setError('Date and location are required');
            return;
        }

        setSending(true);
        setError('');

        try {
            const dateStr = toLocalDateInputValue(date, tenantTimezone);
            const payload = {
                date: dateStr,
                locationId: locationId,
                templateId: parseInt(selectedTemplateId)
            };

            const response = await SmsService.sendDayReminders(payload);

            if (response?.code === 0) {
                const { totalRecipients, optedOutCount } = response.data;
                //const sentCount = totalRecipients - (optedOutCount || 0);
                //toast.success(`Bulk SMS queued - sending to ${sentCount} customer(s)`);
                toast.success(response.message || 'Reminders are being sent');
                onSuccess?.(response.data);
                onClose();
            } else {
                setError(response?.message || 'Failed to send reminders');
            }
        } catch (err) {
            setError(err.message || 'Failed to send reminders');
            console.error('Send error:', err);
        } finally {
            setSending(false);
        }
    };

    const selectedTemplate = templates.find(t => t.id === parseInt(selectedTemplateId));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Send Appointment Reminders</DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={3}>
                    {/* Date & Location (read-only) */}
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Date
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {date ? toLocalDateInputValue(date, tenantTimezone) : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                    Location
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {locationName || `Location ID: ${locationId}`}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Template selection */}
                    <FormControl fullWidth disabled={templateLoading}>
                        <InputLabel>Select SMS Template</InputLabel>
                        <Select
                            value={selectedTemplateId}
                            label="Select SMS Template"
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                        >
                            <MenuItem value="">-- Choose a template --</MenuItem>
                            {templates.map(template => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.templateName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Template preview */}
                    {selectedTemplate && (
                        <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Template Preview:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 1,
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {selectedTemplate.header && `${selectedTemplate.header}\n`}
                                {selectedTemplate.body}
                                {selectedTemplate.footer && `\n${selectedTemplate.footer}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Tokens like {'{patient_name}'} will be replaced with actual data
                            </Typography>
                        </Paper>
                    )}

                    {/* Recipient count preview */}
                    {validationLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {validationResult && !validationLoading && (
                        <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <Typography variant="h6" color="primary">
                                            {validationResult.totalCount}
                                        </Typography>
                                        <Typography variant="caption">Total</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <Typography variant="h6" color="success.main">
                                            {validationResult.validCount}
                                        </Typography>
                                        <Typography variant="caption">Will Receive</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <Typography variant="h6" color="error">
                                            {validationResult.optedOutCount}
                                        </Typography>
                                        <Typography variant="caption">Opted Out</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !selectedTemplateId || validationLoading}
                    startIcon={sending ? <CircularProgress size={20} /> : null}
                >
                    Send Reminders
                </Button>
            </DialogActions>
        </Dialog>
    );
}
